import { Flex, Typography, theme } from 'antd';
import type { GlobalToken } from 'antd';
import { useColorScheme, type ColorSchemePreference } from './color-scheme';
import { baseToken, brandColor, selectionToken, tintaBase } from './theme';

/**
 * O tema em três desenhos da própria plataforma.
 *
 * **Era um `Segmented` de três palavras**, e ele responde "qual está ligado"
 * sem responder "o que eu vou ver". Trocar o tema é uma decisão visual, e a
 * forma que o mercado converge - e que a Voa usa na tela de conta dela - é
 * mostrar a interface em miniatura, uma por opção.
 *
 * **O desenho é feito com divs, e não com uma imagem.** A Voa embute um
 * `<svg>` com um retrato do app dentro; retrato envelhece na primeira mudança
 * de layout e não tem como acompanhar a cor de um módulo. Aqui cada peça sai
 * de um token do tema que ela representa, então a miniatura é sempre a
 * plataforma de verdade - inclusive se a paleta mudar amanhã.
 */

/** As cinco tintas que a miniatura usa, colhidas do tema que ela mostra. */
interface Paleta {
  /** O fundo da área de conteúdo. */
  fundo: string;
  /** O fundo da barra lateral e dos cartões. */
  barra: string;
  /** Texto e ícones, reduzidos a barrinhas. */
  linha: string;
  /** O título da tela, um pouco mais forte que as linhas. */
  titulo: string;
  /** A cor da marca, que aparece no item aceso e no botão. */
  marca: string;
  /** O fio que separa a barra do conteúdo. */
  fio: string;
}

function paletaDe(escuro: boolean): Paleta {
  /*
   * `getDesignToken` é o que permite desenhar o tema **que não está ligado**:
   * o `useToken` só devolve o de agora, e a miniatura do claro precisa das
   * cores do claro mesmo quando a pessoa está no escuro.
   *
   * Os três argumentos são os mesmos que o `TitularisProvider` passa ao
   * `ConfigProvider` - e o `selectionToken` **não é opcional**: é ele que, no
   * escuro, troca o verde da marca pelo tom clareado que o produto usa de
   * verdade. Sem ele o botão da miniatura saía no mesmo verde escuro dos dois
   * lados, e no cartão "Sistema" ele atravessava a emenda parecendo uma peça
   * partida ao meio em vez de duas telas.
   */
  const t: GlobalToken = theme.getDesignToken({
    token: { ...baseToken, ...selectionToken(brandColor, escuro), ...tintaBase(escuro) },
    algorithm: escuro ? theme.darkAlgorithm : theme.defaultAlgorithm,
  });

  /*
   * As barrinhas saem dos tokens de **texto**, e não dos de preenchimento: o
   * `colorFill` é o cinza de um fundo (0,15 de opacidade) e sumia dentro do
   * cartão claro - a miniatura ficava com um papel em branco no lugar da tela.
   * `colorTextQuaternary` e `colorTextTertiary` são a tinta de verdade, na
   * proporção que uma linha de texto ocupa vista de longe.
   */
  return {
    fundo: t.colorBgLayout,
    barra: t.colorBgContainer,
    linha: t.colorTextQuaternary,
    titulo: t.colorTextTertiary,
    marca: t.colorPrimary,
    fio: t.colorSplit,
  };
}

/** A plataforma em miniatura: barra à esquerda, conteúdo à direita. */
function Miniatura({ paleta }: { paleta: Paleta }) {
  const barra = (largura: string, altura: number, cor: string) => (
    <div style={{ width: largura, height: altura, borderRadius: 2, background: cor }} />
  );

  return (
    <Flex style={{ width: '100%', height: '100%', background: paleta.fundo }}>
      <Flex
        vertical
        gap={5}
        style={{
          width: '30%',
          padding: 7,
          background: paleta.barra,
          borderInlineEnd: `1px solid ${paleta.fio}`,
        }}
      >
        {/* O logotipo, o item aceso e dois apagados - a barra do produto. */}
        {barra('70%', 4, paleta.marca)}
        {barra('85%', 3, paleta.titulo)}
        {barra('60%', 3, paleta.linha)}
        {barra('72%', 3, paleta.linha)}
        <div
          style={{
            marginTop: 'auto',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: paleta.linha,
          }}
        />
      </Flex>

      <Flex vertical gap={6} style={{ flex: 1, padding: 10 }}>
        {barra('55%', 5, paleta.titulo)}
        {barra('92%', 3, paleta.linha)}
        {barra('84%', 3, paleta.linha)}
        {barra('88%', 3, paleta.linha)}
        <div
          style={{
            marginTop: 'auto',
            width: '38%',
            height: 9,
            borderRadius: 3,
            background: paleta.marca,
          }}
        />
      </Flex>
    </Flex>
  );
}

const OPCOES: { valor: ColorSchemePreference; rotulo: string }[] = [
  { valor: 'system', rotulo: 'Sistema' },
  { valor: 'light', rotulo: 'Claro' },
  { valor: 'dark', rotulo: 'Escuro' },
];

export function EscolhaDeTema() {
  const { token } = theme.useToken();
  const { preference, setPreference } = useColorScheme();

  const claro = paletaDe(false);
  const escuro = paletaDe(true);

  return (
    /*
      `justify="center"` só faz efeito quando a fileira quebra: no celular os
      três cartões viram uma coluna, e o `maxWidth` deixaria os 220px deles
      encostados na borda esquerda com um vão à direita. Centrados, a coluna
      fica no meio do cartão de ajustes.
    */
    <Flex gap={16} wrap justify="center" role="radiogroup" aria-label="Tema">
      {OPCOES.map(({ valor, rotulo }) => {
        const escolhido = preference === valor;

        return (
          <Flex
            key={valor}
            vertical
            gap={8}
            align="center"
            role="radio"
            aria-checked={escolhido}
            aria-label={rotulo}
            tabIndex={0}
            onClick={() => setPreference(valor)}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter' || evento.key === ' ') {
                evento.preventDefault();
                setPreference(valor);
              }
            }}
            style={{ flex: '1 1 150px', maxWidth: 220, cursor: 'pointer' }}
          >
            <div
              style={{
                position: 'relative',
                width: '100%',
                /*
                 * Proporção de tela, e não medida fixa: no celular os três
                 * cartões dividem a largura que houver, e a miniatura encolhe
                 * junto sem deformar o desenho.
                 */
                aspectRatio: '8 / 5',
                overflow: 'hidden',
                borderRadius: token.borderRadius,
                border: `1px solid ${escolhido ? token.colorPrimary : token.colorBorderSecondary}`,
                // Halo em vez de borda mais grossa: o cartão não pula 1px ao
                // ser escolhido, e o realce continua sendo da cor da marca.
                boxShadow: escolhido ? `0 0 0 3px ${token.colorPrimaryBg}` : undefined,
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
            >
              <Miniatura paleta={valor === 'dark' ? escuro : claro} />

              {/*
                "Sistema" é o único que mostra dois: a metade da direita recebe
                o escuro por cima, recortado ao meio. É o desenho da Voa, e diz
                a coisa certa - esta opção não escolhe um tema, ela obedece ao
                aparelho.
              */}
              {valor === 'system' ? (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    clipPath: 'inset(0 0 0 50%)',
                    /*
                     * O fio da emenda, na tinta do lado escuro. Sem ele as
                     * peças que atravessam o meio - o botão, as linhas de
                     * texto - leem como uma coisa só cortada; com ele, o
                     * cartão lê como duas telas encostadas, que é o que a
                     * opção significa.
                     */
                    borderInlineStart: `1px solid ${escuro.linha}`,
                  }}
                >
                  <Miniatura paleta={escuro} />
                </div>
              ) : null}
            </div>

            <Typography.Text strong={escolhido} style={{ fontSize: token.fontSizeSM }}>
              {rotulo}
            </Typography.Text>
          </Flex>
        );
      })}
    </Flex>
  );
}
